import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { projectService, PROJECT_TYPES, type CreateProjectRequest, type ProjectType, type Project } from '@/lib/services/projectService';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import toast from 'react-hot-toast';
import { cn, getErrorMessage } from '@/lib/utils';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { BoltIcon, ClockIcon } from '@heroicons/react/24/outline';

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectCreated?: (project: Project) => void;
}

export function CreateProjectModal({ open, onOpenChange, onProjectCreated }: CreateProjectModalProps) {
  const { currentWorkspace } = useWorkspace();
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      projectNameInputRef.current?.focus();
      projectNameInputRef.current?.select();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeSelect = (typeId: string) => {
    const type = PROJECT_TYPES.find((projectType) => projectType.id === typeId);
    if (!type?.isAvailable) {
      return;
    }

    setSelectedType(typeId);
  };

  const selectedProjectType = PROJECT_TYPES.find((type) => type.id === selectedType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    if (!selectedType) {
      toast.error('Please select a project type');
      return;
    }

    if (!selectedProjectType?.isAvailable) {
      toast.error('That project type is coming soon');
      return;
    }

    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const projectData: CreateProjectRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        projectType: selectedType,
      };

      const project = await projectService.createProject(projectData);
      
      toast.success('Project created successfully!');
      onProjectCreated?.(project);
      onOpenChange(false);
      
      // Reset form
      setFormData({ name: '', description: '' });
      setSelectedType('');
    } catch (error: unknown) {
      console.error('Failed to create project:', error);
      toast.error(getErrorMessage(error) || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '' });
    setSelectedType('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogClose onClick={() => onOpenChange(false)} />
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-900">
              Project Name *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              ref={projectNameInputRef}
              required
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter project name"
              className="w-full"
            />
          </div>

          {/* Project Type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-900">
              Project Type *
            </Label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {selectedProjectType ? (
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-primary" />
                  <span>
                    Selected type: <span className="font-semibold text-gray-900">{selectedProjectType.name}</span>
                  </span>
                </div>
              ) : (
                <span>Select the project type you want to create.</span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {PROJECT_TYPES.map((type: ProjectType) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTypeSelect(type.id)}
                  aria-pressed={selectedType === type.id}
                  disabled={!type.isAvailable}
                  className={cn(
                    "relative rounded-lg border p-4 text-left transition-shadow",
                    selectedType === type.id
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-gray-200 bg-white",
                    type.isAvailable
                      ? "hover:shadow-md"
                      : "cursor-not-allowed bg-slate-50 text-slate-500"
                  )}
                >
                  <div className="flex items-start">
                    <div
                      className={cn(
                        "mr-4 rounded-lg p-2.5",
                        type.isAvailable ? "bg-primary/10" : "bg-slate-200"
                      )}
                    >
                      {type.isAvailable ? (
                        <BoltIcon className="h-5 w-5 text-primary" />
                      ) : (
                        <ClockIcon className="h-5 w-5 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[15px] font-medium text-gray-900">{type.name}</h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        {type.description}
                      </p>
                    </div>
                  </div>

                  {selectedType === type.id && (
                    <div className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                      Selected
                    </div>
                  )}
                  {!type.isAvailable && (
                    <div className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                      Coming soon
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium text-gray-900">
              Description (Optional)
            </Label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief project description"
              className="w-full px-3 py-2 border border-input bg-background rounded-lg text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.name.trim() || !selectedType}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
